import React, { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import Table from 'antd/es/table';
import { Button, Tag } from 'antd';
import Retry from '@components/common/Retry';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { getColumns } from './columns';
import { Wrapper, FilterTagsContainer, WrappedTableContainer } from './styled';
import { HeaderSection, HeaderTitle } from '../MetricPanel/styled';
import { useFetchAlerts } from './hooks';
import { appliedIntervalAtom, panelsAtom } from '../atoms';
import { getEcoMetricCategoryMap, getFilteredAlertsByMetricCategory } from './utils';

const TableContainer = () => {
  const navigate = useNavigate();
  const interval = useAtomValue(appliedIntervalAtom);
  const panels = useAtomValue(panelsAtom);
  const { alerts, isFetching, isError, refetch } = useFetchAlerts(interval, panels.alerts);

  // State for selected metric categories - Flow Metrics selected by default
  const [selectedCategories, setSelectedCategories] = useState<string[]>(['Flow Metrics']);

  // Get metric category map
  const metricCategoryMap = useMemo(() => getEcoMetricCategoryMap(alerts || []), [alerts]);

  // Filter alerts based on selected categories
  const filteredAlerts = useMemo(() => {
    return getFilteredAlertsByMetricCategory(alerts || [], selectedCategories);
  }, [alerts, selectedCategories]);

  const columns = React.useMemo(() => getColumns(), []);

  // Available categories with counts - always show all 3 categories
  const availableCategories = useMemo(() => {
    const categories: { key: string; label: string }[] = [];

    // Flow Metrics count
    const flowMetricsCount =
      metricCategoryMap['Flow Metrics'] && Object.keys(metricCategoryMap['Flow Metrics']).length > 0
        ? Object.values(metricCategoryMap['Flow Metrics'])
            .flat()
            .reduce((sum: number, item: any) => sum + item.count, 0)
        : 0;
    categories.push({ key: 'Flow Metrics', label: `Flow Metrics (${flowMetricsCount})` });

    // Custom Metrics count
    const customMetricsCount =
      metricCategoryMap['Custom Metrics'] && metricCategoryMap['Custom Metrics'].length > 0
        ? metricCategoryMap['Custom Metrics'].reduce(
            (sum: number, item: any) => sum + item.count,
            0,
          )
        : 0;
    categories.push({ key: 'Custom Metrics', label: `Custom Metrics (${customMetricsCount})` });

    // Standard Metrics count
    const standardMetricsCount =
      metricCategoryMap['Standard Metrics'] && metricCategoryMap['Standard Metrics'].length > 0
        ? metricCategoryMap['Standard Metrics'].reduce(
            (sum: number, item: any) => sum + item.count,
            0,
          )
        : 0;
    categories.push({
      key: 'Standard Metrics',
      label: `Standard Metrics (${standardMetricsCount})`,
    });

    return categories;
  }, [metricCategoryMap]);
  const handleCategoryToggle = (categoryKey: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryKey)) {
        return prev.filter((key) => key !== categoryKey);
      } else {
        return [...prev, categoryKey];
      }
    });
  };

  return (
    <Wrapper style={{ display: panels.alerts ? 'block' : 'none' }}>
      <HeaderSection>
        <HeaderTitle>
          AI Alerts
          <span className="subtitle">( Recent 100 )</span>
        </HeaderTitle>
        <Button
          type="link"
          onClick={() => {
            navigate('/app/app-insights/dashboard/ai-alerts');
          }}
        >
          Show More
        </Button>
      </HeaderSection>

      <FilterTagsContainer>
        {availableCategories.map(({ key, label }) => (
          <Tag.CheckableTag
            key={key}
            checked={selectedCategories.includes(key)}
            onChange={() => handleCategoryToggle(key)}
          >
            {label}
          </Tag.CheckableTag>
        ))}
      </FilterTagsContainer>

      <WrappedTableContainer>
        <Retry progress isLoading={isFetching} showError={isError} fetchData={refetch}>
          <Table
            columns={columns}
            dataSource={filteredAlerts}
            rowKey="alertId"
            pagination={false}
            style={{ fontSize: '12px' }}
            expandable={{
              childrenColumnName: 'alertEvents',
              indentSize: 0,
              expandIcon: ({ expanded, onExpand, record }) =>
                record.alertEvents?.length > 1 ? (
                  expanded ? (
                    <CaretDownOutlined onClick={(e) => onExpand(record, e)} />
                  ) : (
                    <CaretRightOutlined onClick={(e) => onExpand(record, e)} />
                  )
                ) : null,
            }}
          />
        </Retry>
      </WrappedTableContainer>
    </Wrapper>
  );
};

export default TableContainer;
